import './record-mask-display.scss'

import _ from 'lodash'
import { get } from 'lodash'
import { defineComponent, PropType } from 'vue'
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons'

import { MaskCategoryResource, EntityField, getTranslatedString, EntityFieldStatus, isTranslatedString, MaskFieldCompleteResource } from '@skinsoft/types'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { CollapsibleSection } from '@skinsoft/ui'
import { DELETE_IMAGE_CLICK_TOPIC, REPORT_EDITOR_CLOSE_ALL_TABS_TOPIC, REPORT_EDITOR_OPEN_ALL_TABS_TOPIC } from '@skinsoft/bus/src/busTopics'
import { AddMediaEvent, MediasFieldsArea } from '@skinsoft/entity-ui/src/media/medias-fields-area'
import { MaskField } from '../mask-field'

export const RecordMaskDisplay = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sCollapsibleSection: CollapsibleSection,
        sMediasFieldsArea: MediasFieldsArea,
        sMaskField: MaskField,
    },
    props: {
        recordMask: {},
        // entity can be any WEMI/report entity kind this generic display is mounted for, matching
        // the `any` type the callers of this component already use for their own entity field.
        entity: {
            type: Object as PropType<any>,
        },
        isMetaMask: {},
        editing: {},
        getEntityFieldStatus: {},
        showMediaSection: {
            type: Boolean,
            default: true,
        },
        forceMediaUploadInCache: {
            type: Boolean,
            default: false,
        },
    },
    template: require('./record-mask-display.html'),
    data() {
        return {
            // Icons
            caretRightIcon: faCaretRight,
            caretDownIcon: faCaretDown,
            DELETE_IMAGE_CLICK_TOPIC: DELETE_IMAGE_CLICK_TOPIC,
        }
    },
    methods: {
        onCollapseAllClick() {
            this.emitOnBus(REPORT_EDITOR_CLOSE_ALL_TABS_TOPIC);
        },
        onDeployAllClick() {
            this.emitOnBus(REPORT_EDITOR_OPEN_ALL_TABS_TOPIC)
        },
        getCategoryLabel(category: MaskCategoryResource) {
            if (category.label) {
                if(isTranslatedString(category.label)) {
                    return getTranslatedString(category.label, this.getAppLocale())
                }

                // isTranslatedString() above already ruled out the TranslatedString case
                return this.$t(category.label as unknown as string)
            }
            return ""
        },
        updateValue(updateEvent) {
            this.$emit('value-change', { ...updateEvent, subWemi: this.getSubWemi(updateEvent.abstractEntity) })
        },
        /**
         * Return the name of the subfield of 'this.entity' corresponding
         * to the right wemi part for a given entityField
         * @param entityField
         */
        getSubWemi(abstractEntity: string) {
            switch (abstractEntity) {
                case "museumItem":
                    return "item";
                case "museumManifestation":
                    return "manifestation";
                case "work":
                    return "work";
                case "expression":
                    return "expression";
                default:
                    return null
            }

        },
        getFieldValue(entityField: EntityField) {
            // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
            // in this case, it is possible that the right value is inside the item and not the other wemi field.
            // To allow to get the right value in any case, we pass all possible values matching the entity field
            // path to children
            // Example: wemi: { item: { schema: { value: 'OK' } }, expression: { schema: { value: null } } }
            // the current entityField : { path: 'schema', entity: 'expression', entityFields: [...a child that match the value field] }
            // we have to pass wemi.item.schema and wemi.expression.schema to the children so they are able to pick
            // the correct value (because we don't no the children path so we cannot pick the right one here, as both
            // have content)
            // Warning: the path to the cached value can be the same path as online, because if we have a value in cache
            // that is modified online, the cached data is updated

            if (this.isMetaMask) {
                return this.getFieldValueSubWemi(entityField) ??
                    this.getFieldValueItem(entityField) ??
                    this.getFieldValueFromRoot(entityField)
            }
            else {
                return this.getFieldValueFromRoot(entityField)
            }
        },
        getFieldValueSubWemi(entityField: EntityField) {
            // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
            // in this case, it is possible that the right value is inside the item and not the other wemi field.
            // To allow to get the right value in any case, we pass all possible values matching the entity field
            // path to children
            // Example: wemi: { item: { schema: { value: 'OK' } }, expression: { schema: { value: null } } }
            // the current entityField : { path: 'schema', entity: 'expression', entityFields: [...a child that match the value field] }
            // we have to pass wemi.item.schema and wemi.expression.schema to the children so they are able to pick
            // the correct value (because we don't no the children path so we cannot pick the right one here, as both
            // have content)
            // Warning: the path to the cached value can be the same path as online, because if we have a value in cache
            // that is modified online, the cached data is updated

            if(this.isMetaMask) {
                const subWemi = this.getSubWemi(entityField.abstractEntity!)

                if(entityField.pathOffline) {
                    return get(this.entity, `${subWemi}.${entityField.pathOffline}`) ??
                        get(this.entity, `${subWemi}.${entityField.path}`)
                } else {
                    return get(this.entity, `${subWemi}.${entityField.path}`)
                }
            }

            return null
        },
        getFieldValueItem(entityField: EntityField) {
            // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
            // in this case, it is possible that the right value is inside the item and not the other wemi field.
            // To allow to get the right value in any case, we pass all possible values matching the entity field
            // path to children
            // Example: wemi: { item: { schema: { value: 'OK' } }, expression: { schema: { value: null } } }
            // the current entityField : { path: 'schema', entity: 'expression', entityFields: [...a child that match the value field] }
            // we have to pass wemi.item.schema and wemi.expression.schema to the children so they are able to pick
            // the correct value (because we don't no the children path so we cannot pick the right one here, as both
            // have content)
            // Warning: the path to the cached value can be the same path as online, because if we have a value in cache
            // that is modified online, the cached data is updated

            if(this.isMetaMask) {
                const itemSubWemi = this.getSubWemi('museumItem')

                let valueInItem
                if(entityField.pathOffline) {
                    return get(this.entity, `${itemSubWemi}.${entityField.pathOffline}`) ??
                        get(this.entity, `${itemSubWemi}.${entityField.path}`)
                } else {
                    return get(this.entity, `${itemSubWemi}.${entityField.path}`)
                }
            }

            return null
        },
        getFieldValueFromRoot(entityField: EntityField) {
            // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
            // in this case, it is possible that the right value is inside the item and not the other wemi field.
            // To allow to get the right value in any case, we pass all possible values matching the entity field
            // path to children
            // Example: wemi: { item: { schema: { value: 'OK' } }, expression: { schema: { value: null } } }
            // the current entityField : { path: 'schema', entity: 'expression', entityFields: [...a child that match the value field] }
            // we have to pass wemi.item.schema and wemi.expression.schema to the children so they are able to pick
            // the correct value (because we don't no the children path so we cannot pick the right one here, as both
            // have content)
            // Warning: the path to the cached value can be the same path as online, because if we have a value in cache
            // that is modified online, the cached data is updated

            if(entityField.pathOffline) {
                return get(this.entity, entityField.pathOffline) ??
                    get(this.entity, entityField.path)
            }
            return get(this.entity, entityField.path)
        },
        getDataCy(maskField: MaskFieldCompleteResource) {
            let res: string = maskField.entityField?.path ?? ''
            if(maskField.dataCy) {
                res = maskField.dataCy
            }

            return res
        },
        onAddMedia(media: AddMediaEvent) {
            this.$emit('addMedia', media)
        },
        onImageChoice(media) {
            this.$emit('imageChoice', media)
        },
    },
    computed: {
        associatedMediasFieldName() {
            return `associatedMedias`
        },
        itemForMediaArea() {
            if(this.entity.item){
                return this.entity.item;
            }
            return this.entity;
        },
    },
})
